import { useState } from 'react';
import CreateLink from './CreateLink';
import { toast } from 'react-toastify';
import toastConfig from '../../config/Toast';
import useAnalytics from '../../analytics/analytics';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '@/components/ui/button';
import { Link2, Plus, Trash2, Pencil } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';

const MAX_LINKS = 3;

const Links = ({ links, setLinks, setNeedsUpdate, agId, agentName }: { links: any, setLinks: any, setNeedsUpdate: any, agId: string | undefined, agentName: string | undefined }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeLink, setActiveLink] = useState<any>(null);
  const { captureEvent } = useAnalytics();

  const toggleModal = (link?: any) => {
    if (isOpen) {
      setActiveLink(null);
    } else {
      setActiveLink(link);
    }
    setIsOpen(!isOpen);
  };

  const formatUrl = (url: string) => {
    // Remove leading/trailing whitespace
    url = url.trim();
    
    // If URL doesn't start with http:// or https://, prepend https://
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    
    try {
      const urlObj = new URL(url);
      // Check if URL has a valid hostname (at least one dot and valid TLD-like pattern)
      if (!urlObj.hostname.includes('.') || urlObj.hostname.endsWith('.')) {
        return null;
      }
      // Check if hostname only contains valid characters
      if (!/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(urlObj.hostname)) {
        return null;
      }
      return url;
    } catch {
      return null;
    }
  };

  const addLink = (linkName: string, linkDescription: string, linkUrl: string) => {
    if (linkDescription.length > 50) {
      toast.error('Link description must be less than 50 characters', toastConfig);
      return false;
    }
    if (linkName === "") {
      toast.error('Link name is required', toastConfig);
      return false;
    }

    if (linkUrl === "") {
      toast.error('Link URL is required', toastConfig);
      return false;
    }

    const formattedUrl = formatUrl(linkUrl);
    if (!formattedUrl) {
      toast.error('Please enter a valid URL', toastConfig);
      return false;
    }

    if (links.length >= MAX_LINKS) {
      toast.error(`You can only have ${MAX_LINKS} links`, toastConfig);
      return false;
    }
    const link = {
      id: uuidv4(),
      name: linkName,
      description: linkDescription,
      url: formattedUrl
    };
    setLinks((prevState: any) => [...prevState, link]);
    setNeedsUpdate(true);
    captureEvent('agent_link_added', {
      agent_id: agId,
      agent_name: agentName,
    });
    return true;
  };

  const editLink = (id: string, linkName: string, linkDescription: string, linkUrl: string) => {
    const formattedUrl = formatUrl(linkUrl);
    if (!formattedUrl) {
      toast.error('Please enter a valid URL', toastConfig);
      return false;
    }

    let updatedLinks = links.filter((link: any) => link.id !== id);
    updatedLinks = [...updatedLinks, { 
      id: id, 
      name: linkName, 
      description: linkDescription, 
      url: formattedUrl 
    }];
    setLinks(updatedLinks);
    setNeedsUpdate(true);
    return true;
  };

  const deleteLink = (id: string) => {
    setLinks(links.filter((link: any) => link.id !== id));
    setNeedsUpdate(true);
    captureEvent('agent_link_deleted', {
      agent_id: agId,
      agent_name: agentName,
    });
  };

  return (
    <div className="space-y-3">
      {/* Link List */}
      {links && links.length > 0 && (
        <div className="overflow-hidden rounded-md border bg-background divide-y">
          {links.map((link: any) => (
            <div
              key={link.id}
              className="group flex min-h-11 items-center gap-2.5 px-3 py-2 transition-colors hover:bg-muted/30"
            >
              <Link2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <div
                className="flex-1 min-w-0 cursor-pointer"
                onClick={() => toggleModal(link)}
              >
                <div className="flex min-w-0 items-baseline gap-2">
                  <p className="max-w-[40%] shrink-0 truncate text-sm font-medium">{link.name}</p>
                  <p className="min-w-0 truncate text-xs text-muted-foreground">
                    {link.description || link.url}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  onClick={() => toggleModal(link)}
                  aria-label={`Edit ${link.name}`}
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  onClick={() => deleteLink(link.id)}
                  aria-label={`Delete ${link.name}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Link Button */}
      {links.length < MAX_LINKS ? (
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">
            {links.length} of {MAX_LINKS} links
          </span>
          <Button type="button" variant="outline" size="sm" onClick={() => toggleModal()}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add link
          </Button>
        </div>
      ) : (
        <p className="text-right text-xs text-muted-foreground">
          Maximum of {MAX_LINKS} links reached.
        </p>
      )}

      <Dialog open={isOpen} onOpenChange={(open) => !open && toggleModal()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{activeLink ? 'Edit Link' : 'Add Link'}</DialogTitle>
          </DialogHeader>
          <CreateLink
            addLink={addLink}
            editLink={editLink}
            setModalVisible={setIsOpen}
            link={activeLink}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Links;